package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.displayName
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeInferencer

/**
 * M10 Stage 1：拆分自原 TypeConstraintChecker。
 *
 * 维度：可空类型解引用相关诊断 + data 字段访问名校验。
 * 涉及诊断码：
 * - E666：可空类型直接 .field 访问（已有）
 * - **E669（M10 Stage 4 起）**：访问 data 实例的字段名在同文件 / 跨文件 data 字段表中均查不到
 *
 * 对应原文件方法：
 * - checkNullableDereference
 *
 * M10 Stage 6 起：通过 [VarTypeEnv.narrowedTypes]（由 [VarTypeEnv.currentVarEnv] 合并到推断
 * env）自动消费 is/!= null 窄化与 [ExprAst.NonNullAssert] 解包；本 checker 自身无需感知
 * 窄化栈，inferExpr 返回的已是窄化后类型。
 *
 * **E666 与 E669 的优先级**：当 target 是 `Nullable(DataInstance(name))` 时，
 * 若字段名错——同时报 E666（解可空更严重）与 E669（字段错）两条 INFO 是冗余噪声。
 * 取决于 plan 简化策略：本实现遵循"target 拆 Nullable 后是 DataInstance 且字段未命中
 * 时才报 E669"——E666 仍按原规则触发，让用户两条都看到，不再相互抑制。
 */
internal class NullSafetyChecker(
    private val diag: DiagnosticCollector,
    private val env: VarTypeEnv,
) {

    /**
     * E666：可空类型直接解引用（流敏感简化版）。
     *
     * 命中规则：[ExprAst.MemberAccess]（**非 SafeMemberAccess**）的 target 推断类型为
     * `Nullable(T)` 且 inner T != AnyT 时报 INFO。
     *
     * 简化策略：
     * - 名义上是流敏感分析，实现上"看到非 ?. 访问可空类型立即报"——可能误报，但已经
     *   是 INFO 级别，可接受
     * - inner == AnyT（如 `var x = null` 推断为 Nullable(AnyT)）一律放行，避免对绝大多数
     *   动态值产生假阳性
     * - target 推断为非 Nullable 时不触发
     *
     * E669（M10 Stage 4）：在同一入口检查 target 拆 Nullable 后是 [IrType.DataInstance]
     * 且字段名既不在同文件登记的 [VarTypeEnv.localDataFields] 也不在跨文件
     * [VarTypeEnv.crossFileTypes] 的 dataFields 中时报 INFO。
     */
    fun onMemberAccess(access: ExprAst.MemberAccess) {
        val targetType = TypeInferencer.inferExpr(access.target, env.currentVarEnv(), env.crossFileTypes)
        // 1. E666：保留原 Nullable 解引用规则
        if (targetType is IrType.Nullable && targetType.inner != IrType.AnyT) {
            diag.info(
                DslError.E666_NULLABLE_DEREFERENCE_FLOW_INSENSITIVE,
                access.position,
                targetType.displayName(),
                access.member,
            )
        }
        // 2. E669：拆 Nullable 后看是否 DataInstance；字段名两表查不到 → 报 E669
        val unwrapped = if (targetType is IrType.Nullable) targetType.inner else targetType
        if (unwrapped is IrType.DataInstance) {
            val className = unwrapped.className
            val localHit = env.localDataFields[className]?.contains(access.member) == true
            if (localHit) return
            val crossHit = env.crossFileTypes.dataFields[className]?.containsKey(access.member) == true
            if (crossHit) return
            diag.info(
                DslError.E669_DATA_FIELD_TYPE_INFERENCE_FAILED,
                access.position,
                className,
                access.member,
            )
        }
    }
}
